import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Award,
  Heart,
  Target,
  Users,
  Shield,
  Clock,
  ChevronRight,
  Car
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Helmet } from "react-helmet-async";

const values = [
  {
    icon: Shield,
    title: "Siguranță",
    description: "Siguranța ta și a celorlalți participanți la trafic este prioritatea noastră numărul unu."
  },
  {
    icon: Heart,
    title: "Răbdare",
    description: "Înțelegem că fiecare cursant are ritmul său. Te ghidăm cu răbdare la fiecare pas."
  },
  {
    icon: Target,
    title: "Excelență",
    description: "Ne străduim pentru cele mai înalte standarde în pregătirea viitorilor șoferi."
  },
  {
    icon: Users,
    title: "Comunitate",
    description: "Creăm o comunitate de șoferi responsabili și pregătiți pentru drumurile moderne."
  }
];

const stats = [
  { value: "6", label: "Categorii de Permis" },
  { value: "100%", label: "Dedicare" },
  { value: "Flexibil", label: "Program" },
  { value: "Modern", label: "Flotă Auto" }
];

const instructors = [
  {
    name: "Vasîlică Cora",
    role: "Instructor Principal",
    categories: "Cat. B, B+E, C, C+E",
    image: "",
    description: "Instructor cu experiență vastă, specializat în categoriile B, B+E, C și C+E."
  },
  {
    name: "Ionuț Cora",
    role: "Instructor",
    categories: "Cat. B",
    image: "",
    description: "Instructor dedicat pentru categoria B, cu răbdare și atenție la detalii."
  },
  {
    name: "Corneliu Vîgu",
    role: "Instructor",
    categories: "Cat. B",
    image: "",
    description: "Instructor profesionist pentru categoria B, pregătește cursanții pentru succes."
  }
];

export default function AboutPage() {
  return (
    <>
      <Helmet>
        <title>Despre Auto Cora | Școala de Șoferi Fălticeni Suceava</title>

        <meta
          name="description"
          content="Află povestea Auto Cora - școala de șoferi din Fălticeni, Suceava. Instructori dedicați, flotă modernă și pregătire completă pentru permis auto."
        />

        <link rel="canonical" href="https://auto-cora.ro/despre-noi" />

        {/* Open Graph */}
        <meta property="og:title" content="Despre Auto Cora" />
        <meta
          property="og:description"
          content="Cunoaște echipa Auto Cora și modul în care te pregătim pentru permisul auto."
        />
        <meta property="og:url" content="https://auto-cora.ro/despre-noi" />
        <meta property="og:type" content="website" />

        <meta
          name="keywords"
          content="auto cora despre, scoala soferi falticeni, instructori auto suceava, cursuri auto suceava"
        />
      </Helmet>
      <div className="min-h-screen bg-slate-50" data-testid="about-page">
        {/* Hero Section */}
        <section className="bg-white py-16 md:py-24 border-b border-slate-200 overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                className="space-y-6"
              >
                <span className="text-blue-600 font-semibold text-sm uppercase tracking-wider">
                  Despre Noi
                </span>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-slate-900 tracking-tight font-['Archivo']">
                  Pasiunea Noastră Pentru{" "}
                  <span className="text-blue-600">Condus</span>
                </h1>
                <p className="text-slate-600 text-lg leading-relaxed">
                  AUTO CORA este o școală de șoferi fondată cu misiunea de a pregăti
                  șoferi siguri, responsabili și încrezători. Cu o echipă de instructori
                  dedicați și o flotă de mașini moderne, suntem pregătiți să te ghidăm
                  pe drumul tău spre libertatea de a conduce.
                </p>
                <p className="text-slate-600 text-lg leading-relaxed">
                  Înțelegem că obținerea permisului de conducere este un moment important
                  în viața ta, de aceea ne angajăm să îți oferim cea mai bună experiență
                  de învățare, adaptată nevoilor tale individuale.
                </p>
                <Link to="/programari">
                  <Button
                    data-testid="about-cta-button"
                    size="lg"
                    className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-8 shadow-lg shadow-blue-600/20"
                  >
                    Începe Drumul Tău
                    <ChevronRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
                className="relative"
              >
                <img
                  src="https://images.unsplash.com/photo-1678899091039-9950e7f248b5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwzfHxoYXBweSUyMHN0dWRlbnQlMjBkcml2ZXIlMjBpbiUyMG1vZGVybiUyMGNhcnxlbnwwfHx8fDE3NzE3OTc1NzR8MA&ixlib=rb-4.1.0&q=85"
                  alt="Instructaj auto"
                  className="rounded-2xl shadow-2xl w-full object-cover aspect-[4/3]"
                />
                <div className="absolute -bottom-6 -right-6 bg-blue-600 text-white p-6 rounded-xl shadow-xl hidden md:block">
                  <Car className="w-8 h-8 mb-2" />
                  <p className="font-bold text-lg">Flotă Modernă</p>
                  <p className="text-blue-200 text-sm">Vehicule noi și sigure</p>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-16 bg-slate-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {stats.map((stat, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="text-center"
                >
                  <p className="text-4xl md:text-5xl font-black text-white font-['Archivo']">
                    {stat.value}
                  </p>
                  <p className="text-slate-400 mt-2">{stat.label}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Values Section */}
        <section className="py-20 md:py-28 bg-white" data-testid="values-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <span className="text-blue-600 font-semibold text-sm uppercase tracking-wider">
                Valorile Noastre
              </span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mt-4 tracking-tight font-['Archivo']">
                Ce Ne Definește
              </h2>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {values.map((value, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                >
                  <Card className="h-full border border-slate-200 hover:border-blue-500/50 hover:shadow-xl transition-all duration-300 text-center">
                    <CardContent className="p-8">
                      <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center mx-auto mb-6">
                        <value.icon className="w-8 h-8 text-blue-600" />
                      </div>
                      <h3 className="text-xl font-bold text-slate-900 mb-3 font-['Archivo']">
                        {value.title}
                      </h3>
                      <p className="text-slate-600">
                        {value.description}
                      </p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Instructor Section */}
        <section className="py-20 md:py-28 bg-slate-50" data-testid="instructor-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <span className="text-blue-600 font-semibold text-sm uppercase tracking-wider">
                Echipa Noastră
              </span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mt-4 tracking-tight font-['Archivo']">
                Instructori Dedicați
              </h2>
              <p className="text-slate-600 mt-4 max-w-2xl mx-auto">
                Echipa noastră de instructori profesioniști te va ghida cu răbdare spre obținerea permisului.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {instructors.map((instructor, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                >
                  <Card className="overflow-hidden border border-slate-200 hover:shadow-xl transition-shadow h-full">
                    <div className="aspect-[4/3] overflow-hidden">
                      <img
                        src={instructor.image}
                        alt={instructor.name}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <CardContent className="p-6 text-center">
                      <h3 className="text-xl font-bold text-slate-900 font-['Archivo']">
                        {instructor.name}
                      </h3>
                      <p className="text-blue-600 font-medium text-sm mb-1">
                        {instructor.role}
                      </p>
                      <p className="text-slate-500 text-xs font-semibold mb-3">
                        {instructor.categories}
                      </p>
                      <p className="text-slate-600 text-sm">
                        {instructor.description}
                      </p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Fleet Section */}
        <section className="py-20 md:py-28 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="order-2 lg:order-1"
              >
                <div className="grid grid-cols-2 gap-4">
                  <img
                    src="https://images.unsplash.com/photo-1613536491198-a0afa1916b3b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBkcml2aW5nJTIwc2Nob29sJTIwY2FyJTIwZmxlZXR8ZW58MHx8fHwxNzcxNzk3NTc2fDA&ixlib=rb-4.1.0&q=85"
                    alt="Flotă auto"
                    className="rounded-xl shadow-lg w-full aspect-square object-cover"
                  />
                  <img
                    src="https://images.unsplash.com/photo-1769168265020-e2e0c0531e78?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBtb3RvcmN5Y2xlJTIwb24lMjByb2FkfGVufDB8fHx8MTc3MTc5NzU4OHww&ixlib=rb-4.1.0&q=85"
                    alt="Motocicletă"
                    className="rounded-xl shadow-lg w-full aspect-square object-cover mt-8"
                  />
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="order-1 lg:order-2 space-y-6"
              >
                <span className="text-blue-600 font-semibold text-sm uppercase tracking-wider">
                  Flota Noastră
                </span>
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900 tracking-tight font-['Archivo']">
                  Vehicule Moderne și Sigure
                </h2>
                <p className="text-slate-600 text-lg leading-relaxed">
                  Dispunem de o flotă modernă de autovehicule, bine întreținute, pentru pregătirea ta în cele mai bune condiții. Vei învăța pe mașini ușor de condus, adaptate pentru examen și trafic real.
                </p>
                <ul className="space-y-4">
                  {[
                    "Autoturisme echipate cu duble comenzi (cutie manuală și automată)",
                    "Vehicule pentru categoria B+E cu remorcă",
                    "Camioane pentru categoriile C și C+E"
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-center gap-3">
                      <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                        <Award className="w-4 h-4 text-green-600" />
                      </div>
                      <span className="text-slate-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-blue-600">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <Clock className="w-12 h-12 text-white/80 mx-auto mb-6" />
              <h2 className="text-3xl md:text-4xl font-bold text-white font-['Archivo']">
                Gata Să Începi?
              </h2>
              <p className="text-blue-100 mt-4 text-lg">
                Fă primul pas spre permisul tău de conducere. Echipa AUTO CORA te așteaptă!
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center mt-10">
                <Link to="/programari">
                  <Button
                    data-testid="about-booking-btn"
                    size="lg"
                    className="bg-white text-blue-600 hover:bg-blue-50 font-bold px-10"
                  >
                    Programează-te Acum
                    <ChevronRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button
                    data-testid="about-contact-btn"
                    size="lg"
                    variant="outline"
                    className="border-2 border-white text-white hover:bg-white/10 font-semibold bg-transparent"
                  >
                    Contactează-ne
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
}
